import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Mountain, Quote, Tent } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { HOTELS, EXPERIENCES, HERO_IMAGE, GASTRONOMY_IMAGE, EXCURSION_IMAGE, TESTIMONIALS } from "@/lib/data";
import Marquee from "@/components/Marquee";
import HeroSlider from "@/components/HeroSlider";
import DiscoverySection from "@/components/DiscoverySection";
import MapSection from "@/components/MapSection";
import VideoSection from "@/components/VideoSection";
import InstagramSection from "@/components/InstagramSection";
import WhyBookWithUs from "@/components/WhyBookWithUs";
import OptimizedImage from "@/components/OptimizedImage";
import { BerberDiamondDivider, BerberZigzagDivider } from "@/components/BerberDivider";
import { EditableImage } from "@/components/cms/CmsImage";
import PropertyCardGallery from "@/components/PropertyCardGallery";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import XalucaOriginalsBadge from "@/components/XalucaOriginalsBadge";
import XalucaOriginalsCarousel from "@/components/XalucaOriginalsCarousel";
import ExperienceTestimonial, { FLAGSHIP_TESTIMONIALS } from "@/components/ExperienceTestimonial";
import FoundersSection from "@/components/FoundersSection";
import HeritageTimelineCarousel from "@/components/HeritageTimelineCarousel";
import HeritageGallery from "@/components/HeritageGallery";
import HeritagePolaroids from "@/components/HeritagePolaroids";
import GastronomyGallery from "@/components/GastronomyGallery";
import CardCarousel from "@/components/CardCarousel";
import HomeBookingWidget from "@/components/HomeBookingWidget";
import HowToBookSection from "@/components/HowToBookSection";
import HeroVideoTrigger from "@/components/HeroVideoTrigger";
import WelcomeMarquee from "@/components/WelcomeMarquee";
import PageStickyNav from "@/components/PageStickyNav";

const pickLocal = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const HOME_HERO_SLIDES = (lang) => {
  const categoryLabel = (cat) => {
    if (cat === "bivouac") {
      return pickLocal(
        { en: "Bivouac & Desert Camp", fr: "Bivouac & Camp du désert", es: "Bivac y campamento del desierto" },
        lang
      );
    }
    if (cat === "refuge") {
      return pickLocal(
        { en: "Mountain Refuge", fr: "Refuge de montagne", es: "Refugio de montaña" },
        lang
      );
    }
    return pickLocal(
      { en: "Hotel & Kasbah", fr: "Hôtel & Kasbah", es: "Hotel y Kasbah" },
      lang
    );
  };
  const ctaLabel = pickLocal(
    { en: "Discover this address", fr: "Découvrir cette adresse", es: "Descubrir esta dirección" },
    lang
  );
  const secondaryLabel = pickLocal(
    { en: "Plan a stay", fr: "Composer un séjour", es: "Componer una estancia" },
    lang
  );
  const brandLabel = "Grup Xaluca";

  // First slide — a cinematic Mux-hosted MP4 video that frames
  // the whole collection. Mobile falls back to the poster image via the
  // logic inside HeroSlider.
  const videoIntro = {
    image: HERO_IMAGE,
    video: "https://stream.mux.com/HlwGpYi2dBcP007P3601vNiRiY9acrPyBB/medium.mp4",
    videoPoster:
      "https://image.mux.com/HlwGpYi2dBcP007P3601vNiRiY9acrPyBB/thumbnail.jpg?width=1920&time=0",
    badge: pickLocal(
      { en: "Welcome", fr: "Bienvenue", es: "Bienvenidos" },
      lang
    ),
    overline: `${brandLabel} · ${pickLocal({ en: "Southern Morocco", fr: "Sud du Maroc", es: "Sur de Marruecos" }, lang)}`,
    title: pickLocal(
      {
        en: "Hotels, bivouacs, events and services",
        fr: "Hôtels, bivouacs, événements et services",
        es: "Hoteles, bivouacs, eventos y servicios",
      },
      lang
    ),
    titleAccent: pickLocal(
      {
        en: "in the south of Morocco",
        fr: "dans le sud du Maroc",
        es: "en el sur de Marruecos",
      },
      lang
    ),
    sub: pickLocal(
      {
        en: "Nine properties, two families, three hundred hands — and a single promise of welcome across the kasbahs, the dunes and the Atlas peaks.",
        fr: "Neuf adresses, deux familles, trois cents mains — et une seule promesse d'accueil, des kasbahs aux dunes en passant par les sommets de l'Atlas.",
        es: "Nueve direcciones, dos familias, trescientas manos — y una sola promesa de hospitalidad, de las kasbahs a las dunas y a las cumbres del Atlas.",
      },
      lang
    ),
    ctaTo: "/hotels",
    ctaLabel: pickLocal(
      { en: "Discover the hotels", fr: "Découvrir les hôtels", es: "Descubrir los hoteles" },
      lang
    ),
    secondaryTo: "/booking",
    secondaryLabel,
    // Editorial social-proof strip — sits between the sub-headline and the
    // CTAs. Numbers are conservative placeholders the team can override
    // via the CMS (each leaf carries a stable data-cms-role).
    socialProof: {
      avatars: [
        "https://cdn.visitxaluca.com/pexels/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=160&h=160&fit=crop&crop=faces",
        "https://cdn.visitxaluca.com/pexels/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=160&h=160&fit=crop&crop=faces",
        "https://cdn.visitxaluca.com/pexels/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=160&h=160&fit=crop&crop=faces",
        "https://cdn.visitxaluca.com/pexels/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&w=160&h=160&fit=crop&crop=faces",
      ],
      rating: "4.9",
      ratingStars: 5,
      ratingLabel: pickLocal(
        { en: "Guest rating", fr: "Note des clients", es: "Valoración de huéspedes" },
        lang
      ),
      trustText: pickLocal(
        {
          en: "Trusted by 12,000+ travellers across our nine properties",
          fr: "La confiance de plus de 12 000 voyageurs à travers nos neuf adresses",
          es: "La confianza de más de 12.000 viajeros en nuestras nueve direcciones",
        },
        lang
      ),
      scope: "home.hero.social-proof",
    },
  };

  const propertySlides = HOTELS
    .filter((h) => !h.coming_soon)
    .map((h) => {
      const region = pickLocal(
        { en: h.region || "", fr: h.region_fr || h.region || "", es: h.region_es || h.region || "" },
        lang
      );
      const intro = pickLocal(h.intro || {}, lang);
      return {
        image: h.image,
        badge: h.location,
        overline: `${categoryLabel(h.category)} · ${brandLabel}`,
        title: h.name,
        titleAccent: region,
        sub: intro,
        ctaTo: `/hotels/${h.slug}`,
        ctaLabel,
        secondaryTo: "/booking",
        secondaryLabel,
      };
    });

  return [videoIntro, ...propertySlides];
};

const HOME_VIDEOS = (lang) => ({
  featured: {
    id: "feat",
    title: { en: "The Grup Xaluca universe — south of Morocco in two minutes", fr: "L'univers Grup Xaluca — le sud du Maroc en deux minutes", es: "El universo Grup Xaluca — el sur de Marruecos en dos minutos" },
    desc: {
      en: "A short film from our kasbahs in Arfoud to our bivouacs in Erg Chebbi — the people, the dunes, the table, the welcome.",
      fr: "Un court-métrage de nos kasbahs à Arfoud à nos bivouacs de l'Erg Chebbi — les gens, les dunes, la table, l'accueil.",
      es: "Un cortometraje de nuestras kasbahs en Arfoud a nuestros bivacs en Erg Chebbi — la gente, las dunas, la mesa, la acogida.",
    },
    duration: "2:14",
    tag: { en: "Brand film", fr: "Film de marque", es: "Vídeo de marca" },
    poster: "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/4p3qhcbe_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-3.jpg",
  },
  secondary: [
    {
      id: "bivouacs",
      title: { en: "A night under the dunes", fr: "Une nuit sous les dunes", es: "Una noche bajo las dunas" },
      desc: { en: "Bivouacs Xaluca · Erg Chebbi.", fr: "Bivouacs Xaluca · Erg Chebbi.", es: "Bivacs Xaluca · Erg Chebbi." },
      duration: "1:42",
      tag: { en: "Bivouac", fr: "Bivouac", es: "Bivac" },
      poster: "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=1600&q=80",
    },
    {
      id: "gastronomy",
      title: { en: "Inside the kitchens of Arfoud", fr: "Dans les cuisines d'Arfoud", es: "En las cocinas de Arfoud" },
      desc: { en: "From semolina rolling to the mint-tea ritual.", fr: "Du roulage de la semoule au rituel du thé.", es: "Del enrollado de la sémola al ritual del té." },
      duration: "1:58",
      tag: { en: "Gastronomy", fr: "Gastronomie", es: "Gastronomía" },
      poster: "https://images.unsplash.com/photo-1541518763669-27fef04b14ea?auto=format&fit=crop&w=1600&q=80",
    },
    {
      id: "experiences",
      title: { en: "A 4×4 day across the Hammada", fr: "Une journée 4×4 dans la Hammada", es: "Un día en 4×4 por la Hammada" },
      desc: { en: "Drive, valleys, kasbah lunch, return at sundown.", fr: "Conduite, vallées, déjeuner en kasbah, retour au crépuscule.", es: "Conducción, valles, almuerzo en kasbah, regreso al ocaso." },
      duration: "3:06",
      tag: { en: "Experiences", fr: "Expériences", es: "Experiencias" },
      poster: "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/htd6p1fu_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-4.jpg",
    },
  ],
});

const HomePage = () => {
  const { t, lang } = useLanguage();
  const heroSlides = HOME_HERO_SLIDES(lang);
  const videos = HOME_VIDEOS(lang);

  return (
    <div data-testid="page-home">
      {/* HERO — narrative crossfade slider (self-sizing: grows to fit tallest slide) */}
      <section className="relative overflow-hidden">
        <HeroSlider slides={heroSlides} alt="Grup Xaluca · Sud du Maroc" interval={8500} scope="home.hero" />
        <div className="absolute bottom-6 right-6 hidden md:flex items-center gap-2 text-[#FDFBF7]/70 text-[10px] tracking-[0.3em] uppercase z-10 pointer-events-none" data-testid="home-hero-corner-label">
          <span className="w-12 h-px bg-[#FDFBF7]/40" />
          GRUP XALUCA
        </div>
      </section>

      {/* MULTILINGUAL WELCOME MARQUEE — home-page only, sits inline
          immediately below the hero. Same visual language as the
          hotel-card ribbon (dark background, cream tracking-wide
          text, gold amazigh diamonds). */}
      <WelcomeMarquee />

      {/* BOOKING WIDGET — full-width inquiry strip directly under the hero. */}
      <HomeBookingWidget />

      {/* Sticky sub-navigation — jumps to each of the page's main
          narrative blocks. Anchors are set on the `<section>` wrapper
          of each block below via matching `id` attributes. */}
      <PageStickyNav
        items={[
          { id: "section-hotels", label: { en: "Hotels", fr: "Hôtels", es: "Hoteles" } },
          { id: "section-bivouacs", label: { en: "Bivouacs", fr: "Bivouacs", es: "Vivacs" } },
          { id: "section-refuges", label: { en: "Refuges", fr: "Refuges", es: "Refugios" } },
          { id: "section-experiences", label: { en: "Experiences", fr: "Expériences", es: "Experiencias" } },
          { id: "section-heritage", label: { en: "Heritage", fr: "Héritage", es: "Herencia" } },
          { id: "section-gastronomy", label: { en: "Gastronomy", fr: "Gastronomie", es: "Gastronomía" } },
          { id: "section-events", label: { en: "Events", fr: "Événements", es: "Eventos" } },
          { id: "section-testimonials", label: { en: "Testimonials", fr: "Témoignages", es: "Testimonios" } },
          { id: "section-cta", label: { en: "Contact", fr: "Contact", es: "Contacto" } },
        ]}
      />

      {/* DISCOVERY — quick gateway to the four primary corners of the site */}
      <DiscoverySection />

      {/* MARQUEE */}
      <Marquee
        items={[
          "Sahara · Erg Chebbi",
          "Heritage Berber",
          "Atlas Highlands",
          "Family since 1990s",
          "Oasis & Kasbahs",
        ]}
      />

      {/* COLLECTIONS BY CATEGORY */}
      {[
        {
          key: "hotels",
          category: "hotel",
          link: "/hotels",
          overline: t("sections.hotels_overline"),
          title: t("sections.hotels_title"),
          sub: t("sections.hotels_sub"),
          cta: t("common.view"),
          bg: "",
        },
        {
          key: "bivouacs",
          category: "bivouac",
          link: "/bivouacs",
          overline: { en: "Bivouacs & Desert Camps", fr: "Bivouacs & Camps du désert", es: "Vivacs y Campamentos del desierto" }[lang] || "Bivouacs & Desert Camps",
          title: { en: "A night in the Sahara", fr: "Une nuit dans le Sahara", es: "Una noche en el Sáhara" }[lang] || "A night in the Sahara",
          sub: {
            en: "Camel-hair haimas, white luxury suite tents and the silence of the Erg Chebbi — three ways to disappear into the desert.",
            fr: "Haimas en poil de chameau, tentes-suites blanches de luxe et le silence de l'Erg Chebbi — trois façons de disparaître dans le désert.",
            es: "Haimas de pelo de camello, tiendas-suite blancas de lujo y el silencio del Erg Chebbi — tres maneras de desaparecer en el desierto.",
          }[lang],
          cta: { en: "Discover the bivouacs", fr: "Découvrir les bivouacs", es: "Descubrir los vivacs" }[lang] || "Discover the bivouacs",
          bg: "bg-[#F2EBE1]/40",
        },
        {
          key: "refuges",
          category: "refuge",
          link: "/hotels/xaluca-ifess-fess-refuge",
          overline: { en: "Refuge & Mountain Lodge", fr: "Refuge & Lodge de montagne", es: "Refugio y Lodge de montaña" }[lang] || "Refuge & Mountain Lodge",
          title: { en: "Above the valleys", fr: "Au-dessus des vallées", es: "Por encima de los valles" }[lang],
          sub: {
            en: "A high-altitude refuge in the M'Goun massif — base camp for trekkers, astronomers and anyone who reads at altitude.",
            fr: "Un refuge d'altitude dans le massif du M'Goun — camp de base des trekkeurs, des astronomes et de tous ceux qui lisent en altitude.",
            es: "Un refugio de altitud en el macizo del M'Goun — campamento base para senderistas, astrónomos y quien lee a la altura.",
          }[lang],
          cta: { en: "Discover the refuge", fr: "Découvrir le refuge", es: "Descubrir el refugio" }[lang] || "Discover the refuge",
          bg: "",
        },
      ].map((block) => {
        let items = HOTELS.filter((h) => h.category === block.category);
        if (items.length === 0) return null;
        const isWide = items.length === 1;

        // For the bivouacs block, surface "Bivouacs Xaluca" as a hero collection entry
        // (representing the whole desert camp brand) and list the rest as standard cards.
        const COLLECTION_SLUG = "bivouacs-xaluca";
        const isBivouacs = block.category === "bivouac";
        const isRefuge = block.category === "refuge";
        const collection = isBivouacs ? items.find((h) => h.slug === COLLECTION_SLUG) : null;
        const otherCamps = isBivouacs ? items.filter((h) => h.slug !== COLLECTION_SLUG) : [];
        const refugeHero = isRefuge ? items[0] : null;

        const renderCard = (h) => {
          const cardAspect = isWide ? "aspect-[21/9]" : "aspect-[4/5]";
          return (
            <PropertyCardGallery
              key={h.slug}
              hotel={h}
              lang={lang}
              ctaLabel={t("common.view_hotel")}
              aspect={cardAspect}
            />
          );
        };

        return (
          <section
            key={block.key}
            id={`section-${block.key}`}
            className={`relative overflow-hidden py-24 md:py-32 scroll-mt-32 ${isBivouacs ? "bg-[#F2EBE1]/40" : block.bg}`}
            data-testid={`section-${block.key}`}
          >
            {isBivouacs && (
              <img
                src="https://customer-assets.emergentagent.com/job_c24999ff-c14f-412e-baf4-b11c0020fcc2/artifacts/vj5kdsqu_Background%20Dunes.png"
                alt=""
                aria-hidden="true"
                loading="lazy"
                className="pointer-events-none select-none absolute inset-x-0 bottom-0 w-full h-auto z-0 opacity-90"
                data-testid="bivouacs-section-dunes-bg"
                data-no-xaluca-logo="true"
                data-cms-scope="home.bivouacs"
                data-cms-role="dunes-bg"
              />
            )}
            <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
              <div className="grid md:grid-cols-12 gap-8 mb-16">
                <div className="md:col-span-5">
                  <p className="overline">{block.overline}</p>
                  <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                    {block.title}
                  </h2>
                </div>
                <div className="md:col-span-6 md:col-start-7 flex items-end">
                  <p className="text-lg leading-relaxed text-[#5C5248]">{block.sub}</p>
                </div>
              </div>

              {isBivouacs && (
                <>
                  {collection && (
                    <Link
                      to="/bivouacs"
                      className="relative group block overflow-hidden min-h-[560px] md:min-h-[70vh] lg:min-h-[80vh] mb-16 md:mb-20"
                      data-testid="bivouacs-collection-hero"
                      data-cms-scope="bivouacs-collection"
                      data-xaluca-corner="tl"
                    >
                      <img
                        src={collection.image}
                        alt={collection.name}
                        data-cms-role="hero"
                        className="ken-burns absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async"/>
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/95 via-[#1A1513]/55 to-[#1A1513]/15" />
                      <span className="film-grain" aria-hidden="true" />
                      <HeroVideoTrigger
                        title={collection.name}
                        testIdPrefix="bivouacs-collection-hero"
                        position="bottom-right"
                      />
                      <div className="absolute top-6 right-6 md:top-10 md:right-10 text-[10px] tracking-[0.3em] uppercase text-[#FDFBF7]/70 hidden md:block">
                        <span className="w-12 h-px bg-[#FDFBF7]/40 inline-block align-middle mr-3" />
                        {{ fr: "Collection signature", en: "Signature collection", es: "Colección insignia" }[lang] || "Signature collection"}
                      </div>
                      <div className="relative h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pt-20 md:pt-24 lg:pt-28 pb-12 md:pb-16 text-[#FDFBF7]">
                        <p className="overline !text-[#D4A373] flex items-center gap-2">
                          <Tent className="w-3.5 h-3.5" strokeWidth={1.5} />
                          {{ fr: "L'expérience Bivouacs Xaluca", en: "The Bivouacs Xaluca experience", es: "La experiencia Bivouacs Xaluca" }[lang] || "The Bivouacs Xaluca experience"}
                        </p>
                        <h3 className="font-serif-x mt-5 text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.98] max-w-4xl">
                          {/* Each fragment wrapped in its own <span> so
                              CMS edit-mode can target them individually
                              (a bare text node next to an <em> sibling
                              would have no element to bind to). */}
                          <span>{{ fr: "Bivouacs", en: "Bivouacs", es: "Vivacs" }[lang] || "Bivouacs"}</span>{" "}
                          <em className="not-italic text-[#D4A373]">Xaluca</em>
                        </h3>
                        <p className="mt-7 max-w-2xl text-lg md:text-xl text-[#FDFBF7]/85 leading-relaxed">
                          {{
                            fr: "Une seule collection, quatre façons de dormir dans le Sahara — du bivouac aux chandelles aux tentes-suites cinq étoiles. Notre porte d'entrée vers tous les camps Xaluca.",
                            en: "One collection, four ways to sleep in the Sahara — from candle-lit haimas to five-star suite tents. Your doorway into every Xaluca desert camp.",
                            es: "Una sola colección, cuatro maneras de dormir en el Sáhara — del vivac a la luz de las velas a las tiendas-suite cinco estrellas. Su puerta a todos los campamentos Xaluca.",
                          }[lang]}
                        </p>

                        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="bivouacs-collection-camps">
                          {[
                            { slug: "la-belle-etoile-bivouacs", label: { fr: "La Belle Étoile", en: "La Belle Étoile", es: "La Belle Étoile" } },
                            { slug: "kamkamia-luxury-desert-camp", label: { fr: "Kamkamia", en: "Kamkamia", es: "Kamkamia" } },
                            { slug: "merdani-bivouac", label: { fr: "Merdani", en: "Merdani", es: "Merdani" } },
                            { slug: null, label: { fr: "Camps à venir", en: "Future camps", es: "Próximos campamentos" } },
                          ].map((c, ci) => {
                            const camp = c.slug ? HOTELS.find((h) => h.slug === c.slug) : null;
                            const isFuture = !c.slug;
                            return (
                              <span
                                key={`camp-${ci}`}
                                className={`flex items-center gap-2.5 pr-3 border ${
                                  isFuture
                                    ? "border-[#D4A373]/40 text-[#D4A373] italic justify-center py-2 px-3"
                                    : "border-[#FDFBF7]/25 bg-[#1A1513]/55 backdrop-blur-sm text-[#FDFBF7]/95 hover:border-[#D4A373] transition-colors"
                                }`}
                                data-testid={`bivouacs-camp-chip-${c.slug || "future"}`}
                                data-cms-scope={c.slug ? `bivouacs-camp-chip.${c.slug}` : undefined}
                              >
                                {!isFuture && camp ? (
                                  <span className="relative shrink-0 w-12 h-12 overflow-hidden bg-[#1A1513]">
                                    <img
                                      src={camp.image}
                                      alt=""
                                      aria-hidden="true"
                                      className="absolute inset-0 w-full h-full object-cover"
                                      loading="lazy"
                                      decoding="async"
                                      data-no-xaluca-logo="true"
                                      data-cms-role="thumb"
                                    />
                                  </span>
                                ) : null}
                                <span className="text-[10px] sm:text-[11px] tracking-[0.22em] uppercase leading-tight">
                                  {c.label[lang] || c.label.en}
                                </span>
                              </span>
                            );
                          })}
                        </div>

                        <div className="mt-10 inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase bg-[#FDFBF7] text-[#1A1513] px-7 py-4 w-fit group-hover:bg-[#D4A373] transition-colors">
                          {{
                            fr: "Explorer la collection Bivouacs Xaluca",
                            en: "Explore the Bivouacs Xaluca collection",
                            es: "Explorar la colección Bivouacs Xaluca",
                          }[lang] || "Explore the Bivouacs Xaluca collection"}
                          <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                        </div>
                      </div>
                    </Link>
                  )}

                  {otherCamps.length > 0 && (
                    <div data-testid="bivouacs-other-camps">
                      <div className="flex items-baseline justify-between mb-6">
                        <p className="overline">
                          {{
                            fr: "Camps & auberges du désert",
                            en: "Camps & desert auberges",
                            es: "Campamentos y albergues del desierto",
                          }[lang] || "Camps & desert auberges"}
                        </p>
                        <span className="hidden sm:block text-[10px] tracking-[0.3em] uppercase text-[#5C5248]/60">
                          {{ fr: "Découvrir chaque camp", en: "Discover each camp", es: "Descubrir cada campamento" }[lang] || "Discover each camp"}
                        </span>
                      </div>
                      <CardCarousel
                        items={otherCamps}
                        renderItem={renderCard}
                        getKey={(h) => h.slug}
                        edgeFade={false}
                        prevLabel={{ fr: "Camp précédent", en: "Previous camp", es: "Campamento anterior" }[lang] || "Previous camp"}
                        nextLabel={{ fr: "Camp suivant", en: "Next camp", es: "Siguiente campamento" }[lang] || "Next camp"}
                        testIdPrefix="bivouacs-other-camps-carousel"
                      />
                    </div>
                  )}
                </>
              )}

              {isRefuge && refugeHero && (
                <Link
                  to={`/hotels/${refugeHero.slug}`}
                  className="relative group block overflow-hidden min-h-[560px] md:min-h-[70vh] lg:min-h-[80vh]"
                  data-testid="refuge-collection-hero"
                  data-cms-scope={`refuge-collection.${refugeHero.slug}`}
                  data-xaluca-corner="tl"
                >
                  <img
                    src={refugeHero.image}
                    alt={refugeHero.name}
                    data-cms-role="hero"
                    className="ken-burns absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async"/>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/95 via-[#1A1513]/55 to-[#1A1513]/15" />
                  <span className="film-grain" aria-hidden="true" />
                  <HeroVideoTrigger
                    title={refugeHero.name}
                    testIdPrefix="refuge-collection-hero"
                    position="bottom-right"
                  />
                  <div className="absolute top-6 right-6 md:top-10 md:right-10 text-[10px] tracking-[0.3em] uppercase text-[#FDFBF7]/70 hidden md:block">
                    <span className="w-12 h-px bg-[#FDFBF7]/40 inline-block align-middle mr-3" />
                    {{ fr: "Refuge d'altitude", en: "High-altitude refuge", es: "Refugio de altitud" }[lang] || "High-altitude refuge"}
                  </div>
                  <div className="relative h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pt-20 md:pt-24 lg:pt-28 pb-12 md:pb-16 text-[#FDFBF7]">
                    <p className="overline !text-[#D4A373] flex items-center gap-2">
                      <Mountain className="w-3.5 h-3.5" strokeWidth={1.5} />
                      {{ fr: "L'expérience M'Goun", en: "The M'Goun experience", es: "La experiencia M'Goun" }[lang] || "The M'Goun experience"}
                    </p>
                    <h3 className="font-serif-x mt-5 text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.98] max-w-4xl">
                      {{ fr: "Refuge", en: "Refuge", es: "Refugio" }[lang] || "Refuge"}{" "}
                      <em className="not-italic text-[#D4A373]">Ifess Fess</em>
                    </h3>
                    <p className="mt-7 max-w-2xl text-lg md:text-xl text-[#FDFBF7]/85 leading-relaxed">
                      {{
                        fr: "Caché dans les canyons du massif du M'Goun — accessible à dos de mule ou à pied, sous une voûte d'étoiles que seuls les bergers et les astronomes connaissent. Cuisine amazighe au feu de bois, lever de soleil sur les cèdres.",
                        en: "Hidden deep in the M'Goun canyons — reached by mule or on foot, beneath a vault of stars only shepherds and astronomers truly know. Amazigh cuisine cooked over wood fire, sunrise above the cedars.",
                        es: "Escondido en los cañones del macizo del M'Goun — accesible en mula o a pie, bajo una bóveda de estrellas que solo conocen los pastores y los astrónomos. Cocina amazigh al fuego de leña, amanecer sobre los cedros.",
                      }[lang]}
                    </p>

                    <div className="mt-8 flex flex-wrap items-center gap-2.5" data-testid="refuge-collection-tags">
                      {[
                        { fr: "Massif du M'Goun", en: "M'Goun Massif", es: "Macizo del M'Goun" },
                        { fr: "À dos de mule ou à pied", en: "By mule or on foot", es: "En mula o a pie" },
                        { fr: "Cuisine amazighe", en: "Amazigh cuisine", es: "Cocina amazigh" },
                        { fr: "Observation des étoiles", en: "Stargazing", es: "Observación de estrellas" },
                      ].map((c, ci) => (
                        <span
                          key={`refuge-tag-${ci}`}
                          className="text-[10px] tracking-[0.25em] uppercase px-3 py-1.5 border border-[#FDFBF7]/30 text-[#FDFBF7]/85"
                        >
                          {c[lang] || c.en}
                        </span>
                      ))}
                    </div>

                    <div className="mt-10 inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase bg-[#FDFBF7] text-[#1A1513] px-7 py-4 w-fit group-hover:bg-[#D4A373] transition-colors">
                      {{
                        fr: "Explorer le refuge Ifess Fess",
                        en: "Explore the Ifess Fess refuge",
                        es: "Explorar el refugio Ifess Fess",
                      }[lang] || "Explore the Ifess Fess refuge"}
                      <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                    </div>
                  </div>
                </Link>
              )}

              {!isBivouacs && !isRefuge && (
                <div className={`grid grid-cols-1 sm:grid-cols-2 ${isWide ? "" : "lg:grid-cols-3"} gap-6 md:gap-8`}>
                  {items.slice(0, isWide ? 1 : 6).map(renderCard)}
                </div>
              )}

              <div className="mt-12 flex justify-center">
                <Link
                  to={block.link}
                  className="border border-[#2C2621]/20 text-[#2C2621] px-8 py-4 text-xs tracking-[0.2em] uppercase hover:border-[#2C2621] transition-colors"
                  data-testid={`section-${block.key}-cta`}
                >
                  {block.cta} →
                </Link>
              </div>
            </div>
          </section>
        );
      })}

      {/* FOUNDERS — polaroid + handwritten note per founder. Placed right
          before the map so the narrative flows from *the people* who lit
          the path to *the territory* that made it all possible. */}
      <FoundersSection />

      {/* MAP — interactive landmarks + properties */}
      <MapSection />

      {/* VIDEOS — Grup Xaluca brand films */}
      <VideoSection
        overline={{ en: "Films of the south", fr: "Films du sud", es: "Films del sur" }}
        title={{
          en: "The Grup Xaluca universe, in motion.",
          fr: "L'univers Grup Xaluca, en mouvement.",
          es: "El universo Grup Xaluca, en movimiento.",
        }}
        intro={{
          en: "Short editorial films capturing the people, the places and the rituals of the south of Morocco — produced with our chefs, guides and guests.",
          fr: "De courts films éditoriaux qui captent les gens, les lieux et les rituels du sud du Maroc — produits avec nos chefs, guides et hôtes.",
          es: "Cortos editoriales que captan la gente, los lugares y los rituales del sur de Marruecos — producidos con nuestros chefs, guías y huéspedes.",
        }}
        featured={videos.featured}
        secondary={videos.secondary}
        testid="home-videos"
      />

      {/* EXPERIENCES — mirrors the /experiences page structure:
          flagship editorial 2-col grid + secondary Xaluca Originals
          carousel. Keeps the homepage's own header copy so the section
          stays narratively anchored to the rest of the page. */}
      <section id="section-experiences" className="py-24 md:py-32 bg-[#F2EBE1]/50 relative scroll-mt-32" data-testid="section-experiences">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-6">
              <p className="overline">{t("sections.experiences_overline")}</p>
              <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                {t("sections.experiences_title")}
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 flex items-end">
              <p className="text-lg leading-relaxed text-[#5C5248]">
                {t("sections.experiences_sub")}
              </p>
            </div>
          </div>

          {(() => {
            const flagship = EXPERIENCES.filter((e) => !e.secondary);
            const originals = EXPERIENCES.filter((e) => e.secondary);
            const discoverLabel = pickLocal(
              {
                en: "Discover this experience",
                fr: "Découvrir l'expérience",
                es: "Descubrir la experiencia",
              },
              lang,
            );
            return (
              <>
                {/* Flagship editorial grid — same markup as /experiences */}
                <div className="grid md:grid-cols-2 gap-x-8 gap-y-16">
                  {flagship.map((ex) => (
                    <article
                      key={ex.key}
                      className="group"
                      data-testid={`experience-card-${ex.key}`}
                      data-cms-scope={`home.experiences.${ex.key}`}
                    >
                      <Link
                        to={`/experiences/${ex.key}`}
                        className="block relative overflow-hidden aspect-[4/3]"
                        data-testid={`experience-link-${ex.key}`}
                      >
                        <img
                          src={ex.image}
                          alt={ex.title.en}
                          data-cms-role="card"
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" decoding="async"/>
                        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        <XalucaOriginalsBadge
                          variant="dark"
                          className="absolute top-4 left-4"
                          testid={`experience-card-${ex.key}-badge`}
                        />
                        <BrandMonogramEdge
                          size="lg"
                          opacity={0.45}
                          testIdPrefix={`experience-card-${ex.key}-brand`}
                        />
                      </Link>
                      <h3 className="font-serif-x text-3xl md:text-4xl mt-6 tracking-tight">
                        <Link
                          to={`/experiences/${ex.key}`}
                          className="hover:text-[#C16542] transition-colors"
                        >
                          {ex.title[lang] || ex.title.en}
                        </Link>
                      </h3>
                      <p className="mt-4 text-lg text-[#5C5248] leading-relaxed max-w-xl">
                        {ex.desc[lang] || ex.desc.en}
                      </p>
                      <Link
                        to={`/experiences/${ex.key}`}
                        className="mt-6 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#2C2621] hover:text-[#C16542] border-b border-[#2C2621]/30 hover:border-[#C16542] pb-1 transition-colors"
                        data-testid={`experience-discover-${ex.key}`}
                      >
                        {discoverLabel}
                        <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </Link>
                      <ExperienceTestimonial
                        testimonial={FLAGSHIP_TESTIMONIALS[ex.key]}
                        lang={lang}
                        variant="light"
                        testId={`experience-testimonial-${ex.key}`}
                        cmsScope={`home.experiences.${ex.key}.testimonial`}
                      />
                    </article>
                  ))}
                </div>

                {/* Secondary Xaluca Originals — full-width immersive panel
                    mirroring the BespokeExperiencesSection treatment from
                    /experiences (Sahara backdrop + dark gradient + film
                    grain + X monogram edge), so this block reads as a
                    deliberate, editorial pause from the cream Cultural
                    Adventures grid above. */}
                {originals.length > 0 && (
                  <section
                    className="relative isolate text-[#FDFBF7] overflow-hidden w-screen left-1/2 -translate-x-1/2 mt-20 md:mt-28"
                    data-testid="home-experiences-originals"
                    data-cms-scope="home.originals"
                  >
                    {/* Immersive southern Morocco backdrop */}
                    <img
                      src="https://images.unsplash.com/photo-1473580044384-7ba9967e16a0?auto=format&fit=crop&w=2400&q=80"
                      alt=""
                      aria-hidden="true"
                      data-cms-role="backdrop"
                      className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async"/>
                    <div
                      className="absolute inset-0"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(11,8,7,0.82) 0%, rgba(11,8,7,0.92) 38%, rgba(11,8,7,0.96) 100%)",
                      }}
                      aria-hidden="true"
                    />
                    <span className="film-grain !z-[0] opacity-60" aria-hidden="true" />
                    <BrandMonogramEdge size="xl" opacity={0.45} testIdPrefix="home-originals-brand" />

                    <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 lg:py-36">
                      {/* HEADLINE BLOCK — mirrors the BespokeExperiencesSection layout */}
                      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-14 md:mb-20">
                        <div className="lg:col-span-7">
                          <div className="flex items-center gap-3">
                            <XalucaOriginalsBadge variant="dark" testid="home-originals-badge" />
                            <span className="text-[10px] tracking-[0.32em] uppercase text-[#D4A373]">
                              {pickLocal(
                                {
                                  en: "Bespoke · Tailor-made",
                                  fr: "Sur-mesure · Personnalisé",
                                  es: "A medida · Personalizado",
                                },
                                lang,
                              )}
                            </span>
                          </div>
                          <h3 className="font-serif-x mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.02]">
                            {pickLocal(
                              {
                                en: "Beyond the listed experiences — your Southern Morocco, by design.",
                                fr: "Au-delà des expériences listées — votre sud marocain, sur mesure.",
                                es: "Más allá de las experiencias listadas — su sur de Marruecos, a medida.",
                              },
                              lang,
                            )}
                          </h3>
                        </div>
                        <div className="lg:col-span-5 lg:pt-4">
                          <p className="text-base md:text-lg leading-relaxed text-[#FDFBF7]/85">
                            {pickLocal(
                              {
                                en: "Off the obvious trails, this is what our concierges quietly slip to guests who want to see the south the way we live it.",
                                fr: "Hors des sentiers battus, voici ce que nos conciergeries glissent en confidence aux voyageurs qui veulent voir le sud comme nous le vivons.",
                                es: "Fuera de las rutas habituales, esto es lo que nuestros conserjes susurran a los viajeros que quieren ver el sur como nosotros lo vivimos.",
                              },
                              lang,
                            )}
                          </p>
                          <p className="mt-6 text-sm md:text-base leading-relaxed text-[#FDFBF7]/65 border-l border-[#D4A373]/40 pl-5 italic">
                            {pickLocal(
                              {
                                en: "A café, an oasis, a dune kept for those who know to ask.",
                                fr: "Un café, une oasis, une dune réservée à qui sait demander.",
                                es: "Un café, un oasis, una duna reservada para quien sabe pedirla.",
                              },
                              lang,
                            )}
                          </p>
                        </div>
                      </div>

                      <XalucaOriginalsCarousel items={originals} lang={lang} variant="dark" />

                      <div className="mt-12 md:mt-16 flex justify-center">
                        <Link
                          to="/experiences"
                          className="inline-flex items-center gap-2 px-7 py-3.5 border border-[#FDFBF7]/30 hover:border-[#D4A373] text-[11px] tracking-[0.25em] uppercase text-[#FDFBF7] hover:text-[#D4A373] transition-colors"
                          data-testid="home-experiences-view-all"
                        >
                          {pickLocal(
                            {
                              en: "View all experiences",
                              fr: "Voir toutes les expériences",
                              es: "Ver todas las experiencias",
                            },
                            lang,
                          )}
                          <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </Link>
                      </div>
                    </div>
                  </section>
                )}
              </>
            );
          })()}
        </div>
      </section>

      {/* HERITAGE */}
      <section id="section-heritage" className="py-24 md:py-32 relative overflow-hidden scroll-mt-32" data-testid="section-heritage" data-cms-scope="home.heritage">
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <HeritageGallery
              initialSrc="https://images.pexels.com/photos/35771005/pexels-photo-35771005.jpeg?auto=compress&cs=tinysrgb&w=1600"
              testIdPrefix="heritage-gallery"
              scope="home.heritage.gallery"
            />
          </div>
          <div className="md:col-span-5 md:col-start-8">
            <p className="overline">{t("sections.heritage_overline")}</p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl tracking-tight leading-tight">
              {t("sections.heritage_title")}
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-[#5C5248]">
              {t("sections.heritage_text")}
            </p>
            {/* Polaroid memory-wall — CMS-managed via /admin/heritage. */}
            <HeritagePolaroids />
            <Link
              to="/about"
              className="mt-10 inline-block border-b border-[#2C2621]/30 text-[#2C2621] pb-1 text-xs tracking-[0.25em] uppercase hover:border-[#C16542] hover:text-[#C16542] transition-colors"
              data-testid="heritage-learn-more"
            >
              {t("common.learn_more")} →
            </Link>
          </div>
        </div>

        {/* Chronology carousel — sits BELOW the editorial image + text
            row so the visitor gets the "at-a-glance" story of the group
            (11 milestones, 1988 → today) without leaving the homepage.
            Fully responsive: swipe on mobile / tablet, chevron arrows
            on md+ that scroll one card at a time. */}
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 mt-16 md:mt-20">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-8 mb-8 md:mb-10">
            <div className="max-w-2xl">
              <p className="overline">
                {{ fr: "Chronologie", en: "Chronology", es: "Cronología" }[lang] || "Chronology"}
              </p>
              <h3 className="font-serif-x mt-3 text-2xl md:text-3xl tracking-tight leading-tight">
                {{
                  fr: "Trente ans d'histoire, marqués par nos maisons",
                  en: "Three decades of history, told through our houses",
                  es: "Tres décadas de historia, contadas a través de nuestras casas",
                }[lang] || "Three decades of history, told through our houses"}
              </h3>
            </div>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 self-start md:self-auto text-[11px] tracking-[0.25em] uppercase text-[#C16542] hover:text-[#A35133] border-b border-[#C16542]/40 hover:border-[#A35133] pb-1 transition-colors whitespace-nowrap"
              data-testid="heritage-timeline-cta"
            >
              {{
                fr: "Lire l'histoire complète",
                en: "Read the full story",
                es: "Leer la historia completa",
              }[lang] || "Read the full story"}
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>
          </div>
          <HeritageTimelineCarousel testIdPrefix="heritage-timeline" />
        </div>
      </section>

      {/* GASTRONOMY */}
      <section id="section-gastronomy" className="py-24 md:py-32 bg-[#1A1513] text-[#FDFBF7] relative overflow-hidden scroll-mt-32" data-testid="section-gastronomy">
        <div className="absolute inset-0 berber-bg-cross opacity-40 pointer-events-none" aria-hidden="true" />
        <div className="absolute inset-0 moroccan-bg-sandgrid opacity-90 mix-blend-screen pointer-events-none" aria-hidden="true" />
        {/* Architectural arches anchored at the bottom — only on lg+ to avoid clipping the image on mobile. */}
        <div
          className="hidden lg:block absolute bottom-0 inset-x-0 h-40 moroccan-bg-arches opacity-25 pointer-events-none"
          aria-hidden="true"
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <p className="overline !text-[#D4A373]">{t("sections.gastronomy_overline")}</p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl tracking-tight leading-tight">
              {t("sections.gastronomy_title")}
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-[#FDFBF7]/75">
              {t("sections.gastronomy_sub")}
            </p>
            <Link
              to="/gastronomy"
              className="mt-10 inline-block border-b border-[#FDFBF7]/30 text-[#FDFBF7] pb-1 text-xs tracking-[0.25em] uppercase hover:border-[#D4A373] hover:text-[#D4A373] transition-colors"
              data-testid="gastronomy-learn-more"
            >
              {t("common.learn_more")} →
            </Link>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <GastronomyGallery
              initialSrc={GASTRONOMY_IMAGE}
              testIdPrefix="gastronomy-gallery"
              scope="home.gastronomy.gallery"
            />
          </div>
        </div>
      </section>

      {/* EVENTS */}
      <section id="section-events" className="py-24 md:py-32 scroll-mt-32" data-testid="section-events" data-cms-scope="home.events">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="relative overflow-hidden">
            <OptimizedImage
              src={EXCURSION_IMAGE}
              alt={pickLocal({ en: "Camel caravan", fr: "Caravane de chameaux", es: "Caravana de camellos" }, lang)}
              sizes="(min-width: 768px) 100vw, 100vw"
              className="w-full h-[60vh] md:h-[70vh] object-cover"
              objectFit="cover"
              data-cms-role="hero"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1A1513]/85 via-[#1A1513]/40 to-transparent" />
            <div className="absolute inset-0 flex items-center p-8 md:p-16">
              <div className="max-w-xl text-[#FDFBF7]">
                <p className="overline !text-[#D4A373]">{t("sections.events_overline")}</p>
                <h2 className="font-serif-x mt-4 text-4xl md:text-5xl tracking-tight leading-tight">
                  {t("sections.events_title")}
                </h2>
                <p className="mt-6 text-lg text-[#FDFBF7]/85 leading-relaxed">
                  {t("sections.events_sub")}
                </p>
                <Link
                  to="/events"
                  className="mt-8 inline-flex bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-8 py-4 text-xs tracking-[0.2em] uppercase transition-colors"
                  data-testid="events-cta"
                >
                  {t("common.learn_more")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="section-testimonials" className="py-24 md:py-32 bg-[#F2EBE1]/50 relative overflow-hidden scroll-mt-32" data-testid="section-testimonials">
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <BerberDiamondDivider tone="umber" className="max-w-md mx-auto mb-14 md:mb-16 opacity-80" />
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="overline">{t("sections.testimonials_overline")}</p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl tracking-tight leading-tight">
              {t("sections.testimonials_title")}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((tm, i) => (
              <figure
                key={`testimonial-${tm.name || i}`}
                className="bg-[#FDFBF7] border border-[#2C2621]/10 p-8 md:p-10 flex flex-col"
                data-testid={`testimonial-${i}`}
              >
                <Quote className="w-8 h-8 text-[#C16542]" strokeWidth={1.5} />
                <blockquote className="mt-4 font-serif-x text-xl md:text-2xl leading-snug text-[#2C2621] italic">
                  “{tm.text[lang] || tm.text.en}”
                </blockquote>
                <figcaption className="mt-8 text-sm">
                  <div className="font-semibold text-[#2C2621]">{tm.name}</div>
                  <div className="text-[#5C5248] text-xs tracking-wider uppercase mt-1">
                    {tm.place}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>

          {/* CTA — invites guests to the dedicated Wall of Love page. */}
          <div className="mt-14 md:mt-20 flex justify-center" data-testid="testimonials-cta-wrap">
            <Link
              to="/wall-of-love"
              className="bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-8 py-4 text-xs tracking-[0.25em] uppercase inline-flex items-center gap-3 transition-colors"
              data-testid="testimonials-cta-wall-of-love"
            >
              {pickLocal(
                {
                  en: "Explore the Wall of Love",
                  fr: "Explorer le mur d'amour",
                  es: "Explorar el muro del cariño",
                },
                lang
              )}
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* INSTAGRAM — daily journal of the houses */}
      <InstagramSection />

      {/* WHY BOOK WITH US — persuasive value-prop right before the final CTA */}
      <WhyBookWithUs lang={lang} />

      {/* FINAL CTA */}
      <section id="section-cta" className="py-24 md:py-32 relative overflow-hidden scroll-mt-32" data-testid="section-cta">
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif-x text-4xl md:text-6xl tracking-tight leading-[1.05]">
            {t("sections.cta_title")}
          </h2>
          <p className="mt-6 text-lg text-[#5C5248] leading-relaxed max-w-2xl mx-auto">
            {t("sections.cta_text")}
          </p>
          <Link
            to="/booking"
            className="mt-10 inline-flex bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-10 py-4 text-xs tracking-[0.2em] uppercase transition-colors"
            data-testid="final-cta-book"
          >
            {t("hero.cta_primary")}
          </Link>
        </div>
      </section>

      {/* HOW TO BOOK — five editorial steps rendered directly under the
          final CTA so a visitor who reads "Reserva con nosotros" and
          wants to know what happens next has an immediate, on-page
          answer. Reuses the same component surfaced on /booking and the
          hotel detail pages so every booking touchpoint tells the same
          story. */}
      <HowToBookSection />
    </div>
  );
};

export default HomePage;
